// no default React import needed with react-jsx
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material'
import {
  Route as RouteIcon,
  Place as PlaceIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { ActivityFeedItem } from '../types/api'

interface ActivityFeedProps {
  activities: ActivityFeedItem[]
  loading?: boolean
}

export default function ActivityFeed({ activities, loading }: ActivityFeedProps) {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'track_created':
        return <RouteIcon />
      case 'waypoint_created':
        return <PlaceIcon />
      case 'user_registered':
        return <PersonIcon />
      case 'analysis_completed':
        return <AnalyticsIcon />
      default:
        return <PersonIcon />
    }
  }

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'track_created':
        return 'primary'
      case 'waypoint_created':
        return 'success'
      case 'user_registered':
        return 'info'
      case 'analysis_completed':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getActivityDescription = (activity: ActivityFeedItem) => {
    switch (activity.type) {
      case 'track_created':
        return `created track "${activity.track?.name}"`
      case 'waypoint_created':
        return `added waypoint to "${activity.track?.name}" ${activity.details.media_count ? `with ${activity.details.media_count} media files` : ''}`
      case 'user_registered':
        return 'registered as a new user'
      case 'analysis_completed':
        return `completed analysis for waypoint`
      default:
        return 'performed an action'
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader title="Recent Activity" />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader 
        title="Recent Activity" 
        subheader={`${activities.length} recent events`}
        action={
          <Chip 
            label="Live" 
            color="success" 
            size="small" 
            variant="outlined" 
          />
        }
      />
      <CardContent sx={{ p: 0 }}>
        <List>
          {activities.slice(0, 10).map((activity, index) => (
            <ListItem key={activity.id} divider={index < 9}>
              <ListItemAvatar>
                <Avatar 
                  sx={{ 
                    bgcolor: `${getActivityColor(activity.type)}.main`,
                    width: 32,
                    height: 32,
                  }}
                >
                  {getActivityIcon(activity.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" component="span">
                      <strong>{activity.user.display_name}</strong>{' '}
                      {getActivityDescription(activity)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="textSecondary">
                    {format(new Date(activity.timestamp), 'MMM dd, HH:mm:ss')}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
        {activities.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No recent activity
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}